import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { autoOpenOnce } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Lock, Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("session_expired")) {
        sessionStorage.removeItem("session_expired");
        toast.info("Oturumunuz sona erdi. Lütfen tekrar giriş yapın.");
      }
    } catch (e) {
      // sessionStorage kullanılamıyorsa yoksay
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      autoOpenOnce();
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#e9ebee] flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#03231a] p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(#fbbf24 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="relative flex items-center gap-2.5">
          <img src="/logo192.png?v=6" alt="Ai Sigortan" className="h-9 w-9 object-contain" data-testid="brand-logo" />
          <span className="font-heading font-bold text-lg tracking-tight text-amber-400">Ai Sigortan</span>
        </div>
        <div className="relative z-10">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-400 font-semibold">Acente Yönetim Sistemi</p>
          <h1 className="font-heading text-4xl font-semibold tracking-tight leading-tight mt-4">
            Tüm poliçelerinizi
            <br />
            <span className="text-amber-400">tek panelden</span> yönetin.
          </h1>
          <p className="mt-4 text-emerald-100/70 text-sm max-w-md leading-relaxed">
            Üretim, komisyon, hakediş ve yaklaşan yenilemeler; yapay zekâ destekli, güvenli ve her yerden erişilebilir.
          </p>
        </div>
        <div className="flex gap-3 relative z-10">
          {[[ShieldCheck, "SSL şifreli"], [Lock, "Size özel veri"], [Sparkles, "Yapay zekâ"]].map(([I, t]) => (
            <span key={t} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-emerald-50/80">
              <I className="h-3.5 w-3.5 text-amber-400" />{t}
            </span>
          ))}
        </div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-emerald-600/25 blur-3xl" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/logo192.png?v=6" alt="Ai Sigortan" className="h-9 w-9 object-contain" data-testid="brand-logo" />
            <span className="font-heading font-bold text-lg tracking-tight text-amber-600">
              Ai Sigortan
            </span>
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900">
            Tekrar hoş geldiniz
          </h2>
          <p className="text-sm text-zinc-500 mt-1">Hesabınıza giriş yapın</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@eposta.com"
                required
                className="focus-visible:ring-emerald-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="focus-visible:ring-emerald-500"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Giriş Yap
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-emerald-700 hover:underline">
              Şifremi unuttum
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
